<template>
  <v-main>
    <v-container fluid class="pa-6 profile-wrap">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8">
          <v-card variant="outlined" class="pa-6 pa-sm-8 profile-card">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-3" color="primary">mdi-account-circle-outline</v-icon>
              <span class="text-h5 font-weight-bold profile-heading">Your Profile</span>
            </div>
            <div class="text-body-2 text-medium-emphasis mb-8">
              Manage your account details and keep your profile information up to date.
            </div>

            <v-form ref="profileForm" v-model="formValid">
              <v-row>
                <v-col cols="12" md="6">
                  <div class="profile-label mb-2">
                    First name <span v-if="editMode" class="text-red">*</span>
                  </div>
                  <v-text-field
                    v-model="formFirstName"
                    :rules="firstNameRules"
                    hide-details="auto"
                    variant="outlined"
                    density="comfortable"
                    :disabled="!editMode"
                    class="mb-2"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <div class="profile-label mb-2">
                    Last name <span v-if="editMode" class="text-red">*</span>
                  </div>
                  <v-text-field
                    v-model="formLastName"
                    :rules="lastNameRules"
                    hide-details="auto"
                    variant="outlined"
                    density="comfortable"
                    :disabled="!editMode"
                    class="mb-2"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <div class="profile-label mb-2">Email</div>
                  <v-text-field
                    :model-value="email"
                    hide-details="auto"
                    variant="outlined"
                    density="comfortable"
                    disabled
                    class="mb-2"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <div class="profile-label mb-2">
                    Country <span v-if="editMode" class="text-red">*</span>
                  </div>
                  <v-select
                    v-model="formCountry"
                    :items="countryItems"
                    :rules="countryRules"
                    :disabled="!editMode"
                    hide-details="auto"
                    variant="outlined"
                    density="comfortable"
                    class="mb-2"
                  />
                </v-col>
              </v-row>
            </v-form>

            <v-divider class="my-4" />

            <div class="d-flex flex-wrap align-center ga-3">
              <v-btn
                v-if="!editMode"
                color="primary"
                @click="toggleEdit"
              >
                <v-icon class="mr-2" icon="mdi-pencil-outline" />
                Edit Profile
              </v-btn>
              <template v-else>
                <v-btn
                  color="primary"
                  @click="saveChanges"
                  :disabled="!formValid"
                >
                  Save Changes
                </v-btn>
                <v-btn color="secondary" variant="outlined" @click="cancelEdit">
                  Cancel
                </v-btn>
              </template>
            </div>

            <v-alert
              v-if="editMode && !formValid"
              type="warning"
              density="compact"
              variant="tonal"
              class="mt-4"
            >
              Please fill in all required fields before saving.
            </v-alert>
          </v-card>

          <v-card
            id="team-plans"
            variant="outlined"
            class="pa-6 pa-sm-8 profile-card mt-6"
          >
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-3" color="primary">mdi-account-group-outline</v-icon>
              <span class="text-h6 font-weight-bold profile-heading">Team</span>
            </div>
            <div class="text-body-2 text-medium-emphasis mb-6">
              Current team and subscription plan details for your account.
            </div>

            <v-alert
              v-if="teamMessage"
              type="success"
              variant="tonal"
              density="comfortable"
              class="mb-4"
              closable
              @click:close="teamMessage = ''"
            >
              {{ teamMessage }}
            </v-alert>
            <v-alert
              v-if="teamMessageError"
              type="error"
              variant="tonal"
              density="comfortable"
              class="mb-4"
              closable
              @click:close="teamMessageError = ''"
            >
              {{ teamMessageError }}
            </v-alert>

            <v-alert
              v-if="showSubscriptionEndingBanner"
              type="info"
              variant="tonal"
              density="comfortable"
              class="mb-4"
              border="start"
            >
              <div class="font-weight-bold mb-1">Subscription ending</div>
              <div class="text-body-2">
                You will not be charged for the next billing cycle.
                Paid features for your team stay active{{ billingPeriodEndPhrase }}.
              </div>
            </v-alert>

            <div class="team-billing-summary pa-4 pa-sm-5 mb-4">
              <v-row class="team-billing-summary__grid">
                <v-col cols="12" md="6">
                  <div class="profile-label mb-2">Team name</div>
                  <v-text-field
                    v-if="hasTeam"
                    v-model="formTeamName"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    :rules="teamNameRules"
                    class="team-billing-field"
                    bg-color="surface"
                  />
                  <v-text-field
                    v-else
                    :model-value="teamNameDisplay"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    disabled
                    class="team-billing-field"
                    bg-color="surface"
                  />
                  <div v-if="hasTeam" class="d-flex justify-end mt-2">
                    <v-btn
                      color="primary"
                      variant="tonal"
                      size="small"
                      class="text-none"
                      :loading="teamNameSaving"
                      :disabled="!formTeamName || !String(formTeamName).trim()"
                      @click="saveTeamName"
                    >
                      Save team name
                    </v-btn>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="profile-label mb-2">Plan</div>
                  <v-text-field
                    :model-value="teamPlanDisplay"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    disabled
                    class="team-billing-field"
                    bg-color="surface"
                  />
                </v-col>
              </v-row>
            </div>

            <div
              v-if="showPlanUpgradeBanner"
              class="team-upgrade-cta"
              role="region"
              aria-label="Plans and billing"
            >
              <div class="team-upgrade-cta__inner">
                <div class="team-upgrade-cta__lead d-flex align-start ga-4 flex-grow-1">
                  <div class="team-upgrade-cta__icon-wrap" aria-hidden="true">
                    <v-icon size="26" class="team-upgrade-lightning-icon">mdi-lightning-bolt-outline</v-icon>
                  </div>
                  <div class="team-upgrade-cta__copy">
                    <div class="team-upgrade-cta__eyebrow">Plans &amp; billing</div>
                    <div class="team-upgrade-cta__title">{{ upgradeBannerTitle }}</div>
                    <p class="team-upgrade-cta__subtitle text-medium-emphasis mb-0">
                      {{ upgradeBannerSubtitle }}
                    </p>
                  </div>
                </div>
                <v-btn
                  variant="flat"
                  size="large"
                  class="team-upgrade-cta__btn brand-upgrade-cta-btn text-none font-weight-bold"
                  prepend-icon="mdi-view-column-outline"
                  @click="openUpgradePlansDialog"
                >
                  {{ upgradeBannerCta }}
                </v-btn>
              </div>
            </div>
          </v-card>

          <v-dialog
            v-model="showUpgradePlansDialog"
            max-width="920"
            scrollable
          >
            <v-card class="plan-upgrade-dialog-card">
              <div class="plan-upgrade-dialog-header pa-4 pa-sm-6 pb-4">
                <div class="d-flex align-start justify-space-between ga-3">
                  <div class="d-flex align-start ga-3 plan-upgrade-dialog-header__lead">
                    <div class="plan-upgrade-dialog-header__icon flex-shrink-0" aria-hidden="true">
                      <v-icon color="primary" size="28">mdi-compare</v-icon>
                    </div>
                    <div class="plan-upgrade-dialog-header__titles">
                      <div class="text-h6 font-weight-bold profile-heading">Compare plans</div>
                      <p class="text-body-2 text-medium-emphasis mt-1 mb-0 plan-upgrade-dialog-header__blurb">
                        Two paid tiers for healthcare AI visibility—Tier 1 (Visibility) to monitor presence,
                        Tier 2 (Intelligence) to diagnose gaps and prioritise
                      </p>
                    </div>
                  </div>
                  <v-btn
                    icon
                    variant="text"
                    density="comfortable"
                    class="plan-upgrade-dialog-close flex-shrink-0"
                    aria-label="Close"
                    @click="showUpgradePlansDialog = false"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
              <v-divider />
              <v-card-text class="pa-4 pa-sm-6">
                <v-row>
                  <v-col
                    v-for="tier in upgradeTierCards"
                    :key="tier.id"
                    class="d-flex flex-column"
                    cols="12"
                    :md="upgradeTierColSpan"
                  >
                    <div
                      class="plan-tier-card"
                      :class="[
                        tier.accentClass,
                        { 'plan-tier-card--recommended': tier.recommended && !tier.isCurrent },
                        { 'plan-tier-card--current': tier.isCurrent },
                      ]"
                    >
                      <div class="plan-tier-label text-caption font-weight-bold text-medium-emphasis mb-2">
                        {{ tier.tierLabel }}
                      </div>
                      <div class="plan-tier-card__chips mb-2 d-flex flex-wrap ga-2">
                        <v-chip
                          v-if="tier.isCurrent"
                          size="small"
                          color="primary"
                          variant="flat"
                          class="text-none font-weight-bold"
                        >
                          Your plan
                        </v-chip>
                        <v-chip
                          v-else-if="tier.popular"
                          size="small"
                          class="plan-tier-popular-chip text-none font-weight-bold"
                          prepend-icon="mdi-star"
                        >
                          Most popular
                        </v-chip>
                      </div>
                      <div class="text-h6 font-weight-bold plan-tier-card__title">{{ tier.title }}</div>
                      <p class="plan-tier-description text-body-2 text-medium-emphasis mb-3">
                        {{ tier.description }}
                      </p>
                      <div class="plan-tier-price font-weight-bold">{{ tier.priceLine }}</div>
                      <ul class="plan-tier-bullets plan-tier-bullets--checks">
                        <li
                          v-for="(line, idx) in tier.bullets"
                          :key="idx"
                          class="d-flex align-start ga-2"
                        >
                          <v-icon class="plan-tier-check-icon flex-shrink-0" size="18" color="success">
                            mdi-check
                          </v-icon>
                          <span>{{ line }}</span>
                        </li>
                      </ul>
                      <div class="plan-tier-card__cta">
                        <v-btn
                          v-if="tier.showUpgradeCta"
                          block
                          color="primary"
                          variant="flat"
                          class="plan-tier-checkout-btn text-none"
                          :loading="planTierSaving"
                          :disabled="planTierSaving"
                          @click="onChoosePlanTier(tier)"
                        >
                          <span class="plan-tier-checkout-btn__stack">
                            <span class="plan-tier-checkout-btn__primary">{{ tier.checkoutCta.primary }}</span>
                            <span
                              v-if="tier.checkoutCta.secondary"
                              class="plan-tier-checkout-btn__secondary"
                            >{{ tier.checkoutCta.secondary }}</span>
                          </span>
                        </v-btn>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-divider />
              <v-card-actions class="compare-plans-footer pa-4 pa-sm-6 flex-wrap ga-3">
                <v-btn
                  v-if="showCancelDiagnosisPlanOption"
                  variant="text"
                  color="error"
                  density="compact"
                  class="text-none font-weight-bold cancel-plan-btn"
                  :disabled="!hasTeam || cancelPlanSaving || planTierSaving"
                  @click="showCancelPlanConfirm = true"
                >
                  Cancel plan
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-dialog v-model="showCancelPlanConfirm" max-width="480" persistent>
            <v-card>
              <v-card-title class="text-h6 font-weight-bold">Cancel your plan?</v-card-title>
              <v-card-text class="text-body-2">
                {{ cancelPlanConfirmBlurb }}
              </v-card-text>
              <v-card-actions class="pa-4">
                <v-spacer />
                <v-btn variant="text" class="text-none" @click="showCancelPlanConfirm = false">
                  Keep plan
                </v-btn>
                <v-btn
                  color="error"
                  variant="flat"
                  class="text-none font-weight-bold"
                  :loading="cancelPlanSaving"
                  @click="confirmCancelDiagnosisPlan"
                >
                  Cancel plan
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-snackbar v-model="showPlansSnackbar" timeout="8000" color="primary" location="bottom">
            {{ plansSnackbarText }}
          </v-snackbar>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'Profile',
  data() {
    return {
      formCountry: null,
      formFirstName: null,
      formLastName: null,
      formEmail: null,
      editMode: false,
      formValid: false,
      countryItems: [
        'CA', 'US'
      ],
      firstNameRules: [
        v => !!v || 'First name is required',
        v => (v && v.trim().length > 0) || 'First name cannot be empty',
      ],
      lastNameRules: [
        v => !!v || 'Last name is required',
        v => (v && v.trim().length > 0) || 'Last name cannot be empty',
      ],
      countryRules: [
        v => !!v || 'Country is required',
      ],
      formTeamName: '',
      teamNameSaving: false,
      teamNameRules: [
        v => !!v || 'Team name is required',
        v => (v && String(v).trim().length > 0) || 'Team name cannot be empty',
      ],
      teamMessage: '',
      teamMessageError: '',
      showUpgradePlansDialog: false,
      showPlansSnackbar: false,
      plansSnackbarText: '',
      planTierSaving: false,
      showCancelPlanConfirm: false,
      cancelPlanSaving: false,
      /** After Stripe success we replace the URL; avoid immediately re-opening the compare dialog. */
      skipAutoOpenPlansDialogOnce: false,
    }
  },
  computed: {
    ...mapGetters(['teamPlanTierKey']),
    firstName() {
      return this.$store.state.auth.user.first_name
    },
    lastName() {
      return this.$store.state.auth.user.last_name
    },
    country() {
      return this.$store.state.auth.user.country
    },
    email() {
      return this.$store.state.auth.user.email
    },
    teamName() {
      return this.$store.state.auth.user.team_name
    },
    teamPlanTier() {
      return this.$store.state.auth.user.team_plan_tier
    },
    teamNameDisplay() {
      return this.teamName || 'No team'
    },
    teamPlanDisplay() {
      const raw = this.teamPlanTier
      if (raw == null || String(raw).trim() === '') return 'Free'
      return this.formatPlanTier(raw)
    },
    hasTeam() {
      const id = this.$store.state.auth.user?.team_id
      return id != null && id !== ''
    },
    /** Free, Visibility, Intelligence: Plans & Billing + compare dialog. Enterprise: neither. */
    showPlanUpgradeBanner() {
      return ['free', 'visibility', 'intelligence'].includes(this.teamPlanTierKey)
    },
    /** Paid Visibility / Intelligence: cancel via Stripe when subscribed, else QA PATCH to Free. */
    showCancelDiagnosisPlanOption() {
      return (
        this.hasTeam &&
        ['visibility', 'intelligence'].includes(this.teamPlanTierKey) &&
        !this.teamStripeCancelScheduled
      )
    },
    teamStripeCancelScheduled() {
      return this.$store.state.auth.user?.team_stripe_cancel_at_period_end === true
    },
    teamBillingViaStripe() {
      return this.$store.state.auth.user?.team_billing_via_stripe === true
    },
    showSubscriptionEndingBanner() {
      return (
        this.teamStripeCancelScheduled &&
        ['visibility', 'intelligence'].includes(this.teamPlanTierKey)
      )
    },
    billingPeriodEndPhrase() {
      const iso = this.$store.state.auth.user?.team_stripe_current_period_end
      if (!iso) return ''
      try {
        const d = new Date(iso)
        if (Number.isNaN(d.getTime())) return ''
        const formatted = d.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
        return ` until ${formatted}`
      } catch {
        return ''
      }
    },
    cancelPlanConfirmBlurb() {
      if (this.teamBillingViaStripe) {
        return (
          'Your subscription will end after the current billing period. You will not be charged again. ' +
          'Paid features stay active until then.'
        )
      }
      if (this.teamPlanTierKey === 'intelligence') {
        return (
          'Your team will move to the Free plan. You will lose Action Center diagnoses, unlimited provider ' +
          'comparisons, and historical charts tied to Intelligence.'
        )
      }
      return (
        'Your team will move to the Free plan. You will lose Visibility features such as extended monitoring ' +
        'over time and paid-tier provider comparisons.'
      )
    },
    upgradeBannerTitle() {
      if (this.teamPlanTierKey === 'visibility') {
        return 'Unlock full Action Center intelligence'
      }
      if (this.teamPlanTierKey === 'intelligence') {
        return 'Your Intelligence plan'
      }
      return 'Grow from discovery to measurable visibility'
    },
    upgradeBannerSubtitle() {
      if (this.teamPlanTierKey === 'visibility') {
        return 'Open the comparison to move up to Intelligence—full Action Center, every diagnosis, and the same priority colours you already know.'
      }
      if (this.teamPlanTierKey === 'intelligence') {
        return 'Review your tiers below, or cancel to Free from this dialog anytime.'
      }
      return 'Compare Visibility and Intelligence side by side, then continue to secure checkout when you\'re ready.'
    },
    upgradeBannerCta() {
      return this.teamPlanTierKey === 'intelligence'
        ? 'View plans'
        : 'Compare plans & upgrade'
    },
    upgradeTierColSpan() {
      return 6
    },
    upgradeTierCards() {
      const t = this.teamPlanTierKey
      const definitions = [
        {
          id: 'visibility',
          tierLabel: 'Tier 1',
          title: 'Visibility',
          description:
            'Understand and monitor your healthcare AI presence across the platforms your patients use to find care.',
          priceLine: '$299 USD / month',
          accentClass: 'plan-tier-ocean',
          recommended: false,
          popular: false,
          bullets: [
            'Visibility score across ChatGPT, Gemini & Gemini Grounded',
            'Sentiment monitoring over time',
            'Trouble spot matrix with source links',
            'Provider-level ranking tracking',
            'Single health system or clinic tracking',
          ],
        },
        {
          id: 'intelligence',
          tierLabel: 'Tier 2',
          title: 'Intelligence',
          description:
            "Diagnose your AI visibility gaps, understand what's suppressing you, and prioritise every fix by impact and effort.",
          priceLine: '$599 USD / month',
          accentClass: 'plan-tier-teal',
          recommended: true,
          popular: true,
          bullets: [
            'Everything in Visibility',
            'Diagnosis Agent — 14 gap types identified',
            'Action Center with severity × effort prioritisation',
            'Per-finding query impact analysis',
            'Full provider comparison across competitors',
          ],
        },
      ]

      return definitions.map((d) => ({
        ...d,
        isCurrent: t === d.id,
        showUpgradeCta: this.showUpgradeCtaForTier(d.id),
        checkoutCta: this.checkoutCtaLinesForTier(d.id),
      }))
    },
  },
  watch: {
    '$route.query.tab'(tab) {
      if (tab === 'billing') {
        this.$nextTick(() => {
          this.scrollToTeamPlans()
          if (this.skipAutoOpenPlansDialogOnce) {
            this.skipAutoOpenPlansDialogOnce = false
            return
          }
          if (this.showPlanUpgradeBanner) {
            this.showUpgradePlansDialog = true
          }
        })
      }
    },
  },
  async created() {
    const authToken = localStorage.getItem('auth_token')
    if (authToken) {
      await this.$store.dispatch('loginUserWithToken', { auth_token: authToken })
    }
    this.setProfileValues()
    await this.maybeFinalizeStripeCheckout()
    this.$nextTick(() => {
      this.maybeOpenPlansFromQuery()
    })
  },
  methods: {
    maybeOpenPlansFromQuery() {
      if (this.$route.query.tab !== 'billing') return
      this.scrollToTeamPlans()
      if (this.skipAutoOpenPlansDialogOnce) {
        this.skipAutoOpenPlansDialogOnce = false
        return
      }
      if (this.showPlanUpgradeBanner) {
        this.showUpgradePlansDialog = true
      }
    },
    async maybeFinalizeStripeCheckout() {
      const checkout = this.$route.query.checkout
      if (checkout === 'cancel') {
        this.teamMessage = ''
        this.plansSnackbarText = 'Checkout was cancelled.'
        this.showPlansSnackbar = true
        this.$router.replace({ path: '/profile', query: { tab: 'billing' } })
        return
      }
      if (checkout !== 'success') return
      const sessionId = this.$route.query.session_id
      if (!sessionId) {
        this.teamMessageError = 'Checkout succeeded but no session id was returned. Contact support if you were charged.'
        return
      }
      this.teamMessage = ''
      this.teamMessageError = ''
      try {
        await this.$store.dispatch('completeStripeCheckout', { session_id: sessionId })
        const authToken = localStorage.getItem('auth_token')
        if (authToken) {
          await this.$store.dispatch('loginUserWithToken', { auth_token: authToken })
        }
        this.setProfileValues()
        this.skipAutoOpenPlansDialogOnce = true
        this.teamMessage = 'Subscription activated.'
        this.plansSnackbarText = this.teamMessage
        this.showPlansSnackbar = true
        this.showUpgradePlansDialog = false
        this.$router.replace({ path: '/profile', query: { tab: 'billing' } })
      } catch (error) {
        this.teamMessageError =
          error.response?.data?.message ||
          (Array.isArray(error.response?.data?.errors) && error.response.data.errors.join(', ')) ||
          error.response?.data?.error ||
          'Could not confirm checkout. You can retry from Compare plans or contact support.'
      }
    },
    openUpgradePlansDialog() {
      this.showUpgradePlansDialog = true
    },
    async confirmCancelDiagnosisPlan() {
      if (!this.hasTeam) return
      this.cancelPlanSaving = true
      this.teamMessage = ''
      this.teamMessageError = ''
      try {
        if (this.teamBillingViaStripe) {
          const data = await this.$store.dispatch('cancelStripeSubscription')
          const authToken = localStorage.getItem('auth_token')
          if (authToken) {
            await this.$store.dispatch('loginUserWithToken', { auth_token: authToken })
          }
          this.setProfileValues()
          this.showCancelPlanConfirm = false
          this.showUpgradePlansDialog = false
          this.teamMessage = data.message || 'You will not be charged for the next billing cycle.'
          this.plansSnackbarText = this.teamMessage
          this.showPlansSnackbar = true
        } else {
          await this.$store.dispatch('updateTeamPlan', { plan_tier: 'free' })
          const authToken = localStorage.getItem('auth_token')
          if (authToken) {
            await this.$store.dispatch('loginUserWithToken', { auth_token: authToken })
          }
          this.setProfileValues()
          this.showCancelPlanConfirm = false
          this.showUpgradePlansDialog = false
          this.teamMessage = 'Plan cancelled. Your team is on the Free plan.'
          this.plansSnackbarText = this.teamMessage
          this.showPlansSnackbar = true
        }
      } catch (error) {
        this.teamMessageError =
          error.response?.data?.message ||
          (Array.isArray(error.response?.data?.errors) && error.response.data.errors.join(', ')) ||
          error.response?.data?.error ||
          'Unable to cancel plan.'
      } finally {
        this.cancelPlanSaving = false
      }
    },
    async onChoosePlanTier(tier) {
      if (!tier?.id) return
      if (this.teamPlanTierKey === 'free') {
        await this.startStripeCheckoutForTier(tier)
        return
      }
      if (
        this.teamPlanTierKey === 'visibility' &&
        tier.id === 'intelligence' &&
        this.teamBillingViaStripe
      ) {
        await this.upgradeStripeSubscriptionToIntelligenceFlow(tier)
        return
      }
      this.planTierSaving = true
      this.teamMessage = ''
      this.teamMessageError = ''
      try {
        await this.$store.dispatch('updateTeamPlan', { plan_tier: tier.id })
        const authToken = localStorage.getItem('auth_token')
        if (authToken) {
          await this.$store.dispatch('loginUserWithToken', { auth_token: authToken })
        }
        this.setProfileValues()
        this.showUpgradePlansDialog = false
        this.teamMessage = `Plan updated to ${tier.title}.`
        this.plansSnackbarText = this.teamMessage
        this.showPlansSnackbar = true
      } catch (error) {
        this.teamMessageError =
          error.response?.data?.message ||
          (Array.isArray(error.response?.data?.errors) && error.response.data.errors.join(', ')) ||
          error.response?.data?.error ||
          'Unable to update plan.'
      } finally {
        this.planTierSaving = false
      }
    },
    async upgradeStripeSubscriptionToIntelligenceFlow(tier) {
      this.planTierSaving = true
      this.teamMessage = ''
      this.teamMessageError = ''
      try {
        const data = await this.$store.dispatch('upgradeStripeSubscriptionToIntelligence')
        const authToken = localStorage.getItem('auth_token')
        if (authToken) {
          await this.$store.dispatch('loginUserWithToken', { auth_token: authToken })
        }
        this.setProfileValues()
        this.showUpgradePlansDialog = false
        this.teamMessage =
          data.message ||
          `Plan updated to ${tier.title}. Stripe may charge a prorated amount for the rest of this period.`
        this.plansSnackbarText = this.teamMessage
        this.showPlansSnackbar = true
      } catch (error) {
        this.teamMessageError =
          error.response?.data?.message ||
          (Array.isArray(error.response?.data?.errors) && error.response.data.errors.join(', ')) ||
          error.response?.data?.error ||
          'Unable to upgrade subscription.'
      } finally {
        this.planTierSaving = false
      }
    },
    async startStripeCheckoutForTier(tier) {
      this.planTierSaving = true
      this.teamMessage = ''
      this.teamMessageError = ''
      try {
        const data = await this.$store.dispatch('createStripeCheckoutSession', {
          plan_tier: tier.id,
        })
        const url = data?.url
        if (!url) {
          throw new Error('Missing checkout URL')
        }
        window.location.assign(url)
      } catch (error) {
        this.teamMessageError =
          error.response?.data?.message ||
          (Array.isArray(error.response?.data?.errors) && error.response.data.errors.join(', ')) ||
          error.response?.data?.error ||
          error.message ||
          'Unable to start checkout.'
      } finally {
        this.planTierSaving = false
      }
    },
    showUpgradeCtaForTier(tierId) {
      const t = this.teamPlanTierKey
      if (t === tierId) return false
      if (tierId === 'visibility') return t === 'free'
      if (tierId === 'intelligence') return t === 'free' || t === 'visibility'
      return false
    },
    checkoutCtaLinesForTier(tierId) {
      if (this.teamPlanTierKey === 'free') {
        if (tierId === 'intelligence') {
          return {
            primary: 'Upgrade',
            secondary: 'Unlock the Action Center',
          }
        }
        if (tierId === 'visibility') {
          return {
            primary: 'Current Trial',
            secondary: 'Upgrade for Daily Tracking',
          }
        }
      }
      if (tierId === 'intelligence') {
        return {
          primary: 'Upgrade',
          secondary: 'Unlock the Action Center',
        }
      }
      if (tierId === 'visibility') {
        return {
          primary: 'Visibility',
          secondary: '',
        }
      }
      return { primary: 'Upgrade', secondary: '' }
    },
    scrollToTeamPlans() {
      const el = document.getElementById('team-plans')
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    toggleEdit() {
      this.editMode = true
    },
    cancelEdit() {
      this.setProfileValues()
      this.editMode = false
    },
    async saveChanges() {
      // Validate the form before submitting
      const { valid } = await this.$refs.profileForm.validate()
      
      if (!valid) {
        return // Don't save if validation fails
      }

      // Ensure no empty values
      if (!this.formFirstName || !this.formFirstName.trim() || 
          !this.formLastName || !this.formLastName.trim() || 
          !this.formCountry) {
        return // Extra safety check
      }

      try {
        await this.$store.dispatch('updateUser', {
          selection: {
            first_name: this.formFirstName.trim(),
            last_name: this.formLastName.trim(),
            country: this.formCountry,
          }
        })
        this.setProfileValues()
        this.editMode = false
      } catch (error) {
        console.error('Failed to update profile:', error)
        // You could add a user-facing error message here
      }
    },
    setProfileValues() {
      this.formCountry = this.country
      this.formFirstName = this.firstName
      this.formLastName = this.lastName
      this.formEmail = this.email
      this.formTeamName = this.teamName || ''
    },
    async saveTeamName() {
      const name = (this.formTeamName || '').trim()
      if (!name || !this.hasTeam) return
      this.teamNameSaving = true
      this.teamMessage = ''
      this.teamMessageError = ''
      try {
        await this.$store.dispatch('updateTeamName', { name })
        const authToken = localStorage.getItem('auth_token')
        if (authToken) {
          await this.$store.dispatch('loginUserWithToken', { auth_token: authToken })
        }
        this.setProfileValues()
        this.teamMessage = 'Team name updated.'
      } catch (error) {
        this.teamMessageError =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Unable to update team name.'
      } finally {
        this.teamNameSaving = false
      }
    },
    formatPlanTier(planTier) {
      const value = String(planTier || '').trim().toLowerCase()
      if (!value) return ''
      return value.charAt(0).toUpperCase() + value.slice(1)
    }
  }
}
</script>

<style scoped>
.profile-wrap {
  max-width: 1200px;
}

.profile-card {
  border-radius: 16px;
  border-color: var(--brand-sand-300) !important;
  box-shadow: 0 2px 8px rgba(13, 30, 53, 0.06);
}

.profile-heading {
  color: var(--brand-ink-950);
}

.profile-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--brand-ink-800);
}

.team-billing-summary {
  border-radius: 14px;
  border: 1px solid var(--brand-sand-300);
  background: linear-gradient(180deg, var(--brand-sand-50) 0%, #ffffff 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.team-billing-summary__grid {
  margin-top: 0;
  margin-bottom: 0;
}

.cancel-plan-btn {
  letter-spacing: 0.01em;
}

.compare-plans-footer {
  justify-content: flex-start;
}

.team-upgrade-cta {
  margin-top: 4px;
  border-radius: 14px;
  border: 1px solid rgba(77, 157, 223, 0.32);
  background: linear-gradient(
    125deg,
    var(--brand-ocean-100) 0%,
    #ffffff 48%,
    var(--brand-teal-100) 100%
  );
  box-shadow:
    0 4px 20px rgba(13, 30, 53, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset;
}

.team-upgrade-cta__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px 20px;
  padding: 18px 20px;
}

.team-upgrade-cta__icon-wrap {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  box-shadow: 0 2px 10px rgba(13, 30, 53, 0.06);
}

.team-upgrade-lightning-icon {
  color: var(--brand-ocean-600) !important;
}

.team-upgrade-cta__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--brand-ocean-600);
  margin-bottom: 6px;
}

.team-upgrade-cta__title {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.35;
  color: var(--brand-ink-950);
  margin-bottom: 6px;
}

.team-upgrade-cta__subtitle {
  font-size: 13px;
  line-height: 1.5;
  max-width: 520px;
  color: var(--brand-ink-800);
}

.team-upgrade-cta__btn {
  flex-shrink: 0;
}

.plan-upgrade-dialog-card {
  border-radius: 16px;
  border: 1px solid var(--brand-sand-300);
  overflow: hidden;
}

.plan-upgrade-dialog-header {
  background: linear-gradient(180deg, var(--brand-sand-50) 0%, #ffffff 100%);
  border-bottom: 1px solid var(--brand-sand-300);
  overflow: visible;
}

.plan-upgrade-dialog-header__lead {
  flex: 1 1 auto;
  min-width: 0;
}

.plan-upgrade-dialog-header__titles {
  flex: 1 1 auto;
  min-width: 0;
}

.plan-upgrade-dialog-header__icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  box-shadow: 0 2px 8px rgba(13, 30, 53, 0.06);
}

.plan-upgrade-dialog-header__blurb {
  max-width: 100%;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
}

.plan-upgrade-dialog-close {
  margin-top: -4px;
}

.plan-tier-card {
  border-radius: 14px;
  border: 1px solid var(--brand-sand-300);
  padding: 18px 18px 16px;
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  box-sizing: border-box;
}

.plan-tier-card__title {
  color: var(--brand-ink-950);
}

.plan-tier-description {
  line-height: 1.5;
  margin: 0;
}

.plan-tier-price {
  font-size: 1.2rem;
  line-height: 1.3;
  color: var(--brand-ink-950);
  margin-bottom: 4px;
}

.plan-tier-popular-chip {
  background: var(--brand-ocean-600) !important;
  color: #fff !important;
}

.plan-tier-popular-chip :deep(.v-icon) {
  color: #fff !important;
}

.plan-tier-ocean {
  border-color: rgba(77, 157, 223, 0.35);
  background: linear-gradient(180deg, var(--brand-ocean-100) 0%, #ffffff 100%);
}

.plan-tier-teal {
  border-color: rgba(26, 172, 176, 0.35);
  background: linear-gradient(180deg, var(--brand-teal-100) 0%, #ffffff 100%);
}

.plan-tier-card--recommended {
  box-shadow:
    0 0 0 2px var(--brand-ocean-600),
    0 10px 24px rgba(13, 30, 53, 0.12);
}

.plan-tier-card--current {
  box-shadow:
    0 0 0 2px rgba(33, 105, 168, 0.35),
    0 6px 16px rgba(13, 30, 53, 0.08);
}

.plan-tier-card__cta {
  width: 100%;
  margin-top: auto;
  padding-top: 16px;
  flex-shrink: 0;
}

.plan-tier-checkout-btn {
  width: 100%;
  height: auto !important;
  min-height: 3.75rem;
}

.plan-tier-checkout-btn :deep(.v-btn__overlay),
.plan-tier-checkout-btn :deep(.v-btn__underlay) {
  border-radius: inherit;
}

.plan-tier-checkout-btn :deep(.v-btn__content) {
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  white-space: normal;
  text-align: center;
  padding: 0.75rem 1rem 0.875rem !important;
}

.plan-tier-checkout-btn :deep(.v-btn__loader) {
  position: absolute;
}

.plan-tier-checkout-btn__stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  line-height: 1.3;
  padding: 0.125rem 0 0;
  white-space: normal;
}

.plan-tier-checkout-btn__primary {
  font-size: 0.9375rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.plan-tier-checkout-btn__secondary {
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.3;
  opacity: 0.96;
}

.plan-tier-bullets {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1 1 auto;
}

.plan-tier-bullets--checks li {
  position: static;
  padding-left: 0;
  margin-bottom: 0.65rem;
}

.plan-tier-check-icon {
  margin-top: 2px;
}

@media (max-width: 600px) {
  .team-upgrade-cta__btn {
    width: 100%;
  }

  .team-upgrade-cta__inner {
    flex-direction: column;
    align-items: stretch;
  }
}

.v-btn {
  text-transform: none;
  border-radius: 100px;
}
</style>